import medicationSupportedProgrammePack from '@public/img/checkout/medication-supported-programme-pack.png';
import trackAppPreview from '@public/img/how-it-works/track-app-preview-desktop.png';

const currentDate = new Date();
currentDate.setFullYear(currentDate.getFullYear() + 10);
const priceValidUntil = currentDate.toISOString().split('T')[0];

/**
 * This is used to add structured data to the normal programme page.
 * Structured data is used by Google to display rich results in search results.
 * @see https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data
 */
export const normalProgrammePageStructuredData = `{
  "@context": "http://schema.org",
  "@type": "Product",
  "name": "Second Nature Weight Loss Programme",
  "image": "${trackAppPreview.src}",
  "description": "A behavioural change programme, tailored to individual needs.",
  "brand": {
    "@type": "Brand",
    "name": "Second Nature",
    "sameAs": [
      "https://www.facebook.com/secondnature.io",
      "https://www.instagram.com/secondnature_health",
      "https://www.youtube.com/@SecondNature_health",
      "https://linkedin.com/company/besecondnature",
      "https://www.trustpilot.com/review/secondnature.io",
      "https://play.google.com/store/apps/details?id=uk.co.ourpath.android",
      "https://apps.apple.com/gb/app/second-nature/id885435960",
      "https://www.tiktok.com/@secondnature_health",
      "https://x.com/snhealth_uk"
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "bestRating": 5,
    "worstRating": 1,
    "reviewCount": "6916",
  },
  "offers": {
    "@type": "Offer",
    "price": "28",
    "priceCurrency": "GBP",
    "availability": "http://schema.org/InStock",
    "itemCondition": "https://schema.org/NewCondition",
    "url": "https://www.secondnature.io/how-it-works",
    "priceValidUntil": "${priceValidUntil}",
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": "0",
        "currency": "GBP"
      },
    }
  }
}`;

/**
 * This is used to add structured data to the Mounjaro page.
 * Structured data is used by Google to display rich results in search results.
 * @see https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data
 */
export const mounjaroPageStructuredData = `{
  "@context": "http://schema.org",
  "@type": "Product",
  "name": "Second Nature Mounjaro Weight Loss Programme",
  "image": "${medicationSupportedProgrammePack.src}",
  "description": "A comprehensive weight loss programme combining Mounjaro GLP-1 medication with behavioural change support, tailored to individual needs.",
  "brand": {
    "@type": "Brand",
    "name": "Second Nature",
    "sameAs": [
      "https://www.facebook.com/secondnature.io",
      "https://www.instagram.com/secondnature_health",
      "https://www.youtube.com/@SecondNature_health",
      "https://linkedin.com/company/besecondnature",
      "https://www.trustpilot.com/review/secondnature.io",
      "https://play.google.com/store/apps/details?id=uk.co.ourpath.android",
      "https://apps.apple.com/gb/app/second-nature/id885435960",
      "https://www.tiktok.com/@secondnature_health",
      "https://x.com/snhealth_uk"
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "bestRating": 5,
    "worstRating": 1,
    "reviewCount": "6916",
    "@id": "https://www.secondnature.io/mounjaro-weight-loss-programme#aggregateRating"
  },
  "offers": {
    "@type": "Offer",
    "price": "129",
    "priceCurrency": "GBP",
    "availability": "http://schema.org/InStock",
    "itemCondition": "https://schema.org/NewCondition",
    "url": "https://www.secondnature.io/mounjaro-weight-loss-programme",
    "priceValidUntil": "${priceValidUntil}",
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": "0",
        "currency": "GBP"
      }
    }
  }
}`;

/**
 * Generates structured data for an article page.
 *
 * @param headline - The headline of the article.
 * @param datePublished - The date the article was published.
 * @param imageUrl - The URL of the article image.
 * @param authorName - The name of the article author.
 * @param authorUrl - The URL of the article author.
 * @param dateModified - The date the article was last modified (optional).
 * @returns The generated structured data as a string.
 */
export const articlePageStructuredData = (
  headline: string,
  datePublished: Date,
  imageUrl: string,
  authorName: string,
  authorUrl: string,
  dateModified?: Date,
): string => `{
  "@context": "http://schema.org",
  "@type": "BlogPosting",
  "headline": "${headline}",
  "datePublished": "${datePublished.toISOString()}",${
    dateModified ? `\n  "dateModified": "${dateModified.toISOString()}",` : ''
  }
  "image": ["${imageUrl}"],
  "author": [
    {
      "@type": "Person",
      "name": "${authorName}",
      "url": "${authorUrl}"
    }
  ]
}`;
